// loginErrors.ts — qué le decimos a quien no puede iniciar sesión.
//
// Round 2026-10-28. El dueño reportó que la app instalada en el celular «no me deja loguearme»
// y no había forma de saber por qué: el login convertía TODO fallo en un mensaje genérico
// («Error en el inicio de sesión») o, peor, en una recarga muda de la pantalla (ver
// apiClient.ts: un 401 del login disparaba el cierre de sesión forzado). Acá cada causa tiene
// su propio mensaje, y la misma clasificación alimenta el evento de Sentry.
//
// Es una función PURA (sin red, sin DOM) para poder probar cada causa por separado.

export type LoginFailureKind =
  | "red" // sin conexión o el navegador bloqueó la petición (CORS / origen)
  | "demasiados_intentos" // 429
  | "turnstile_vencido" // el token de Cloudflare venció o ya se usó
  | "turnstile_fallo" // Cloudflare rechazó el token
  | "turnstile_no_disponible" // Cloudflare no respondió al servidor
  | "credenciales" // 401
  | "sin_permiso" // 403 (rol sin login, propiedades deshabilitadas)
  | "origen_o_csrf" // 403 por CSRF / origen no permitido
  | "servidor" // 5xx
  | "desconocido";

export interface LoginFailure {
  kind: LoginFailureKind;
  /** Texto para mostrar al usuario (español neutro, sin jerga). */
  message: string;
  status?: number;
  /** `code` que devuelve el backend, si lo hay (TURNSTILE_EXPIRED, RATE_LIMITED, ...). */
  code?: string;
  /** Segundos de espera (solo 429). */
  retryAfterSeconds?: number;
  /** ¿Hay que pedirle un token nuevo a Turnstile antes de reintentar? */
  renewTurnstile: boolean;
}

export interface LoginFailureInput {
  /** Estado HTTP; ausente si la petición ni siquiera llegó. */
  status?: number;
  /** Cuerpo JSON de la respuesta (si se pudo leer). */
  body?: unknown;
  /** La petición falló sin respuesta (`fetch` lanzó TypeError). */
  networkError?: boolean;
  /** `navigator.onLine` en el momento del fallo. */
  online?: boolean;
  /** Valor del header `Retry-After` (solo legible si el backend lo expone por CORS). */
  retryAfterHeader?: string | null;
}

/** Error lanzado por `authApi.login`; lleva la clasificación ya hecha. */
export class LoginError extends Error {
  readonly failure: LoginFailure;
  constructor(failure: LoginFailure) {
    super(failure.message);
    this.name = "LoginError";
    this.failure = failure;
  }
}

/** «14 min», «45 s», «1 min». Redondea hacia arriba para no prometer menos de lo que falta. */
export function formatearEspera(segundos: number): string {
  const s = Math.max(1, Math.ceil(segundos));
  if (s < 60) return `${s} s`;
  return `${Math.ceil(s / 60)} min`;
}

function leerCampo(body: unknown, campo: string): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const v = (body as Record<string, unknown>)[campo];
  return typeof v === "string" && v.trim() ? v : undefined;
}

function leerNumero(body: unknown, campo: string): number | undefined {
  if (!body || typeof body !== "object") return undefined;
  const v = (body as Record<string, unknown>)[campo];
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

export function clasificarFalloLogin(input: LoginFailureInput): LoginFailure {
  const { status, body, networkError, online, retryAfterHeader } = input;
  const code = leerCampo(body, "code");
  const serverMsg = leerCampo(body, "message");

  // Sin respuesta: sin red, o el navegador cortó la petición (típico de CORS cuando la app se
  // abre desde un origen que el backend no tiene en ALLOWED_ORIGINS).
  if (networkError || status === undefined) {
    return {
      kind: "red",
      renewTurnstile: true,
      message:
        online === false
          ? "Sin conexión a internet. Revisa tu red e inténtalo de nuevo."
          : "No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo en unos segundos.",
    };
  }

  if (status === 429) {
    const header = retryAfterHeader ? Number(retryAfterHeader) : NaN;
    const retryAfterSeconds =
      leerNumero(body, "retryAfterSeconds") ??
      (Number.isFinite(header) && header > 0 ? header : 15 * 60);
    return {
      kind: "demasiados_intentos",
      status,
      code,
      retryAfterSeconds,
      renewTurnstile: false,
      message: `Demasiados intentos fallidos. Espera ${formatearEspera(retryAfterSeconds)} antes de volver a intentarlo.`,
    };
  }

  // Turnstile: el backend responde 400 con `code`. Se reconoce también por el texto, por si
  // el backend desplegado todavía no manda `code`.
  const esTurnstile =
    (code !== undefined && code.startsWith("TURNSTILE_")) ||
    (status === 400 && /verificaci[oó]n de seguridad/i.test(serverMsg ?? ""));
  if (esTurnstile) {
    if (code === "TURNSTILE_EXPIRED") {
      return {
        kind: "turnstile_vencido",
        status,
        code,
        renewTurnstile: true,
        message:
          "La verificación de seguridad venció. Ya la renovamos: espera un momento y vuelve a presionar «Iniciar sesión».",
      };
    }
    if (code === "TURNSTILE_UNAVAILABLE") {
      return {
        kind: "turnstile_no_disponible",
        status,
        code,
        renewTurnstile: true,
        message:
          "No pudimos contactar el servicio de verificación de seguridad. Inténtalo de nuevo en unos segundos.",
      };
    }
    return {
      kind: "turnstile_fallo",
      status,
      code,
      renewTurnstile: true,
      message:
        "La verificación de seguridad falló. Espera a que se renueve (unos segundos) e inténtalo de nuevo.",
    };
  }

  if (status === 401) {
    return {
      kind: "credenciales",
      status,
      code,
      renewTurnstile: true,
      // No dice cuál de los dos falló (anti-enumeración), pero sí qué revisar: en el celular el
      // teclado suele poner mayúscula inicial o un espacio al final sin que se note.
      message:
        "Correo o contraseña incorrectos. Revisa las mayúsculas y que no sobren espacios, y vuelve a intentarlo.",
    };
  }

  if (status === 403) {
    if (/csrf|origen|origin/i.test(serverMsg ?? "")) {
      return {
        kind: "origen_o_csrf",
        status,
        code,
        renewTurnstile: true,
        message:
          "El servidor rechazó la conexión (origen o sesión no válidos). Cierra la app, ábrela de nuevo e inténtalo; si sigue igual, avisa al administrador.",
      };
    }
    return {
      kind: "sin_permiso",
      status,
      code,
      renewTurnstile: true,
      message: serverMsg ?? "Tu cuenta no tiene permiso para iniciar sesión.",
    };
  }

  if (status >= 500) {
    return {
      kind: "servidor",
      status,
      code,
      renewTurnstile: true,
      message:
        status === 502 || status === 503 || status === 504
          ? `El servidor no está disponible en este momento (error ${status}). Inténtalo de nuevo en unos minutos.`
          : `El servidor tuvo un problema (error ${status}). Inténtalo de nuevo en unos minutos.`,
    };
  }

  if (status < 400) {
    // 2xx/3xx sin el cuerpo esperado: un proxy o portal cautivo contestó por el servidor.
    return {
      kind: "desconocido",
      status,
      code,
      renewTurnstile: true,
      message: "El servidor respondió algo inesperado. Inténtalo de nuevo en unos segundos.",
    };
  }

  return {
    kind: "desconocido",
    status,
    code,
    renewTurnstile: true,
    message: serverMsg ?? `No se pudo iniciar sesión (error ${status}).`,
  };
}
